import type { ContentNode, FieldNode, ScoreField } from "@/types/template";

interface ScoreGroupResult {
  sum: number; // sum of observed scores (after bilateral pair aggregation)
  min: number; // minimum observed score across slots
  max: number; // maximum possible total (sum of slot maxes)
  avg: number; // sum / count of scored slots
  count: number; // number of score slots in the group
}

interface Slot {
  observed: number | undefined;
  slotMax: number;
}

/** Aggregate paired score fields into a single slot using pairAgg (min/max). */
function aggregatePair(pairFields: ScoreField[], values: Record<string, unknown>): Slot {
  const agg = pairFields[0]?.pairAgg ?? "min";
  const observed = pairFields
    .map((f) => values[f.id])
    .filter((v): v is number => typeof v === "number");
  let aggValue: number | undefined;
  if (observed.length > 0) {
    aggValue = agg === "min" ? Math.min(...observed) : Math.max(...observed);
  }
  return { observed: aggValue, slotMax: pairFields[0]?.max ?? 0 };
}

/** Build score slots from grouped fields, collapsing paired fields into one slot each. */
function buildSlots(fields: ScoreField[], values: Record<string, unknown>): Slot[] {
  const pairMap = new Map<string, ScoreField[]>();
  const unpaired: ScoreField[] = [];

  for (const f of fields) {
    if (f.pairId) {
      const list = pairMap.get(f.pairId) ?? [];
      list.push(f);
      pairMap.set(f.pairId, list);
    } else {
      unpaired.push(f);
    }
  }

  const slots: Slot[] = unpaired.map((f) => {
    const v = values[f.id];
    return { observed: typeof v === "number" ? v : undefined, slotMax: f.max };
  });

  for (const [, pairFields] of pairMap) {
    slots.push(aggregatePair(pairFields, values));
  }

  return slots;
}

/**
 * Calculate statistics for all score fields belonging to a named scoregroup.
 * Fields with a pairId are aggregated by pairAgg (min/max) before contributing
 * one slot to the result, matching the bilateral L/R scoring convention.
 */
export function calcScoreGroup(
  content: ContentNode[],
  groupId: string,
  values: Record<string, unknown>,
): ScoreGroupResult {
  const fields = content
    .filter((n): n is FieldNode => n.type === "field" && n.field.type === "score")
    .map((n) => n.field as ScoreField)
    .filter((f) => f.scoregroupId === groupId);

  if (fields.length === 0) {
    return { avg: 0, count: 0, max: 0, min: 0, sum: 0 };
  }

  const slots = buildSlots(fields, values);
  const scoredValues = slots.map((s) => s.observed).filter((v): v is number => v !== undefined);
  const sum = scoredValues.reduce((s, v) => s + v, 0);
  const max = slots.reduce((s, slot) => s + slot.slotMax, 0);
  const min = scoredValues.length > 0 ? Math.min(...scoredValues) : 0;
  const avg = scoredValues.length > 0 ? sum / scoredValues.length : 0;
  const count = slots.length;

  return { avg, count, max, min, sum };
}
